import { useSyncExternalStore } from 'react';

/**
 * Board rendering mode. `high` is the 3D board with shadows, glow and idle
 * animation; `low` drops those for older devices; `flat` is the SVG map.
 */
export type Graphics = 'high' | 'low' | 'flat';

const KEY = 'horde:graphics';
const ORDER: Graphics[] = ['high', 'low', 'flat'];

export const graphicsLabel: Record<Graphics, string> = {
  high: '3D high',
  low: '3D low',
  flat: 'Flat 2D',
};

let webgl2: boolean | null = null;
/** The 3D board needs WebGL 2 (iOS 15+, every current desktop browser). */
export function hasWebGL2(): boolean {
  if (webgl2 === null) {
    try {
      webgl2 = !!document.createElement('canvas').getContext('webgl2');
    } catch {
      webgl2 = false;
    }
  }
  return webgl2;
}

let current: Graphics = (() => {
  // Dev shortcut: ?graphics=high|low|flat for this page load.
  const q = new URLSearchParams(window.location.search).get('graphics');
  if (q === 'high' || q === 'low' || q === 'flat') return q;
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'high' || v === 'low' || v === 'flat') return v;
  } catch {
    // storage unavailable
  }
  return 'high';
})();

const listeners = new Set<() => void>();

export function getGraphics(): Graphics {
  return hasWebGL2() ? current : 'flat';
}

/** `persist: false` changes the mode for this session only (used by the crash fallback). */
export function setGraphics(g: Graphics, persist = true): void {
  current = g;
  if (persist) {
    try {
      localStorage.setItem(KEY, g);
    } catch {
      // ignore
    }
  }
  listeners.forEach((l) => l());
}

/** Cycle high → low → flat; skips the 3D modes when WebGL 2 is missing. */
export function cycleGraphics(): void {
  if (!hasWebGL2()) return;
  setGraphics(ORDER[(ORDER.indexOf(current) + 1) % ORDER.length]!);
}

export function useGraphics(): Graphics {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getGraphics,
    getGraphics,
  );
}

// ---------------------------------------------------------------------------
// Follow camera: the 3D board eases over to fights and card effects.

const FOLLOW_KEY = 'horde:follow';
let follow: boolean = (() => {
  try {
    const v = localStorage.getItem(FOLLOW_KEY);
    if (v === '1' || v === '0') return v === '1';
  } catch {
    // storage unavailable
  }
  // A moving camera is motion; start still for players who asked for less.
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
})();
const followListeners = new Set<() => void>();

export function toggleFollow(): void {
  follow = !follow;
  try {
    localStorage.setItem(FOLLOW_KEY, follow ? '1' : '0');
  } catch {
    // ignore
  }
  followListeners.forEach((l) => l());
}

export function useFollow(): boolean {
  return useSyncExternalStore(
    (l) => {
      followListeners.add(l);
      return () => followListeners.delete(l);
    },
    () => follow,
    () => follow,
  );
}
